import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { Skeleton } from 'antd';
import { useQueryDisciplinesListFiles } from 'Hooks/disciplines/useQueryDisciplinesListFiles';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import Modal from 'Components/UI/Modal';
import Button from 'Components/UI/Button';
import ListContainer from 'Components/UI/Containers/ListContainer/ListContainer';
import { Icon, IconName } from 'Components/UI';
import styles from './ExclusaoDisciplina.module.scss';

export interface IExclusaoDisciplina {
  visible?: boolean;
  isLoading?: boolean;
  id?: number;
  name?: string;
  icon: string;
  constructionSiteId: number;
  folderId: string;
  onCancel?: () => void;
  onConfirmDelete: () => void;
  onConfirmOpenDiscipline: () => void;
}

const ExclusaoDisciplina: FC<Props> = ({
  visible,
  isLoading,
  id,
  name,
  icon,
  constructionSiteId,
  folderId,
  onCancel,
  onConfirmDelete,
  onConfirmOpenDiscipline,
}) => {
  const { t } = useTranslation('disciplinas');
  const {
    data: disciplineFiles,
    isLoading: isLoadingDisciplineFiles,
    error: listFilesError,
  } = useQueryDisciplinesListFiles({
    constructionSiteId,
    folderId: folderId,
  });
  const hasListError = !!listFilesError;

  const footerButtons = hasListError ? (
    <div>
      <Button height={37} onClick={onCancel}>
        <span style={{ padding: '0 18px' }}>{t('exclusaoDisciplina.cancelDelete')}</span>
      </Button>
    </div>
  ) : (
    <div>
      {disciplineFiles !== 0 ? (
        <>
          <Button height={37} onClick={onCancel}>
            <span style={{ padding: '0 18px' }}>{t('exclusaoDisciplina.cancelDelete')}</span>
          </Button>
          <Button
            type="primary"
            height={37}
            onClick={() => onConfirmOpenDiscipline()}
            loading={isLoading || isLoadingDisciplineFiles}
          >
            <span style={{ padding: '0 18px' }}>{t('exclusaoDisciplina.goToDiscipline')}</span>
          </Button>
        </>
      ) : (
        <>
          <Button height={37} onClick={onCancel}>
            <span style={{ padding: '0 18px' }}>{t('exclusaoDisciplina.cancelDelete')}</span>
          </Button>
          <Button
            type="primary"
            height={37}
            onClick={() => onConfirmDelete()}
            loading={isLoading || isLoadingDisciplineFiles}
          >
            <span style={{ padding: '0 18px' }}>{t('exclusaoDisciplina.confirmDelete')}</span>
          </Button>
        </>
      )}
    </div>
  );

  return (
    <Modal
      width={798}
      title={t('exclusaoDisciplina.modalTitle')}
      visible={visible}
      onCancel={onCancel}
      footerButtons={footerButtons}
    >
      {(isLoading || isLoadingDisciplineFiles) ? (
        <div style={{ padding: 20 }}>
          <Skeleton active paragraph={{ rows: 2 }} />
        </div>
      ) : (
        <div className={styles['textWrap']}>
          {hasListError ? (
            <div className={styles['margin']}>
              {t('exclusaoDisciplina.errorListFiles')}
            </div>
          ) : disciplineFiles !== 0 ? (
            <div className={styles['margin']}>
              {t('exclusaoDisciplina.warningHasFiles')}
            </div>
          ) : (
            <>
              <div className={styles['margin']}>
                {t('exclusaoDisciplina.warningPermanent')}
              </div>
              <div>{t('exclusaoDisciplina.confirmQuestion')}</div>
            </>
          )}
          <ListContainer height={51} key={id} className={styles['listDisciplineWrap']}>
            <Icon
              color="cinzaPadrao"
              icon={icon as IconName}
              customSize={18}
            />
            <span className={styles['title']}>{name}</span>
            {!hasListError && disciplineFiles !== 0 ? (
              <span className={styles['infoFile']}>
                {t('exclusaoDisciplina.filesFound', { count: disciplineFiles })}
              </span>
            ) : null}
          </ListContainer>
        </div>
      )}
    </Modal>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  selectedEditRowKeys: state.listagemArquivos.selectedEditRowKeys
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & IExclusaoDisciplina;

export default connector(ExclusaoDisciplina);
